import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Sparkles, FileText, Loader2, X, ArrowRight } from "lucide-react";

export function ImportProgramModal({ onClose, onImported }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!text.trim() && !file) { toast.error("Paste your programming or choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (text.trim()) fd.append("text", text.trim());
      if (file) fd.append("file", file);
      const { data } = await api.post("/programs/import", fd, { timeout: 180000 });
      setResult(data);
      onImported();
      toast.success(`"${data.title}" imported`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed — try a smaller chunk");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-5" style={{ background: "rgba(10,9,8,0.72)" }} onClick={onClose} data-testid="import-modal">
      <div className="card-luxe max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="overline flex items-center gap-2"><Sparkles size={12} /> AI Import</div>
            <h2 className="font-serif text-3xl mt-2">Import your programming</h2>
            <p className="text-sm text-secondary-luxe mt-2 max-w-lg">
              Upload a spreadsheet, PDF, or doc — or paste your programming as text. I'll turn it into a
              structured program and add any new exercises to your library automatically.
            </p>
          </div>
          <button className="btn-ghost" onClick={onClose} data-testid="close-import-modal"><X size={16} /></button>
        </div>

        {result ? (
          <div className="mt-6 space-y-4" data-testid="import-result">
            <div className="p-5" style={{ background: "var(--surface-2)" }}>
              <div className="overline">Imported</div>
              <h3 className="font-serif text-2xl mt-2">{result.title}</h3>
              <p className="text-sm text-secondary-luxe mt-2">
                {result.duration_weeks} weeks · {result.workout_count} workouts ·{" "}
                {result.exercises_created.length} new exercises added to your library
                {result.exercises_matched.length > 0 ? ` · ${result.exercises_matched.length} matched existing` : ""}
              </p>
              {result.exercises_created.length > 0 && (
                <p className="text-xs text-tertiary-luxe mt-3">New: {result.exercises_created.join(", ")}</p>
              )}
            </div>
            <div className="flex gap-3 flex-wrap">
              <button className="btn-primary" onClick={() => navigate(`/coach/programs/${result.program_id}`)} data-testid="review-imported-btn">
                Review &amp; edit program <ArrowRight size={13} />
              </button>
              <button className="btn-secondary" onClick={() => { setResult(null); setText(""); setFile(null); }} data-testid="import-another-btn">
                Import another
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <div>
              <label className="label-luxe">Upload a file (PDF, Word, Excel, CSV, TXT)</label>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx,.csv,.txt,.md" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
              <div className="flex items-center gap-3 mt-2">
                <button type="button" className="btn-secondary text-xs" onClick={() => fileRef.current?.click()} data-testid="choose-file-btn">
                  <Upload size={13} /> Choose file
                </button>
                {file && <span className="text-xs text-secondary-luxe flex items-center gap-1"><FileText size={12} /> {file.name}</span>}
              </div>
            </div>
            <div>
              <label className="label-luxe">And / or paste your programming</label>
              <textarea className="input-luxe mt-2" rows={8} value={text} onChange={(e) => setText(e.target.value)}
                placeholder={"Week 1\nDay 1 — Lower Body\nBack Squat 4x8, rest 90s\nRDL 3x10\nWalking Lunges 3x10/leg\n\nDay 3 — Upper Body\nBench Press 4x8..."}
                data-testid="import-text-input" />
            </div>
            <button className="btn-primary w-full justify-center" onClick={run} disabled={busy} data-testid="run-import-btn">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Reading your programming… (up to a minute)</> : <><Sparkles size={14} /> Import with AI</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
